import {create} from 'zustand';
import {Store} from '../models/store.model';
import {storeFactory} from './storeFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

export interface StoreSelectionState {
  stores: Store[];
  selectedStore?: Store;
  selectedStoreDetails?: Store; // Full store details with contact info
  isLoading: boolean;
  isLoadingDetails: boolean;
  error?: string;
}

interface StoreSelectionStore extends StoreSelectionState {
  // Actions
  setStores: (stores: Store[]) => void;
  setSelectedStore: (store?: Store) => void;
  setSelectedStoreDetails: (store?: Store) => void;
  setLoading: (loading: boolean) => void;
  setLoadingDetails: (loading: boolean) => void;
  setError: (error?: string) => void;
  fetchStoreDetails: (storeId: string) => Promise<void>;
  clearSelection: () => void;
  reset: () => void;
}

// دوال مساعدة للمقارنة
const areStoresEqual = (currentStores: Store[], newStores: Store[]): boolean => {
  if (currentStores.length !== newStores.length) {
    return false;
  }

  const currentIds = new Set(currentStores.map(s => s.id));
  const newIds = new Set(newStores.map(s => s.id));

  return currentIds.size === newIds.size && [...currentIds].every(id => newIds.has(id));
};

// الحالة الافتراضية
const defaultState: StoreSelectionState = {
  stores: [],
  selectedStore: undefined,
  selectedStoreDetails: undefined,
  isLoading: false,
  isLoadingDetails: false,
  error: undefined
};

export const useStoreSelectionStore = create<StoreSelectionStore>((set, get) => {
  // Initialize use case using factory
  const {getStoreDetails} = storeFactory.getUseCases();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  return {
    ...defaultState,

    setStores: (stores) => {
      const currentStores = get().stores;

      if (!areStoresEqual(currentStores, stores)) {
        set({stores});
      }
    },

  setSelectedStore: (store) => {
    const currentSelectedStore = get().selectedStore;

    if (currentSelectedStore?.id !== store?.id) {
      set({selectedStore: store, selectedStoreDetails: undefined}); // Clear details when store changes
    }
  },

  setSelectedStoreDetails: (store) => {
    set({selectedStoreDetails: store});
  },

  setLoading: (loading) => {
    const currentLoading = get().isLoading;
    if (currentLoading !== loading) {
      set({isLoading: loading});
    }
  },

  setLoadingDetails: (loading) => {
    const currentLoading = get().isLoadingDetails;
    if (currentLoading !== loading) {
      set({isLoadingDetails: loading});
    }
  },

  setError: (error) => {
    const currentError = get().error;
    if (currentError !== error) {
      set({error});
    }
  },

  fetchStoreDetails: async (storeId: string) => {
    const {setLoadingDetails, setSelectedStoreDetails, setError} = get();

    setLoadingDetails(true);
    setError(undefined);

    try {
      const fullStore = await getStoreDetails.execute(storeId);
      setSelectedStoreDetails(fullStore);
      // Always replace selectedStore with full details so the details panel gets creditInfo, images, etc.
      set((prev) => ({ ...prev, selectedStore: fullStore }));
    } catch (error) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setSelectedStoreDetails(undefined);
    } finally {
      setLoadingDetails(false);
    }
  },

  clearSelection: () => {
    const currentSelectedStore = get().selectedStore;
    if (currentSelectedStore) {
      set({selectedStore: undefined, selectedStoreDetails: undefined});
    }
  },

  reset: () => {
    set(defaultState);
  }
  };
});
